import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Modal from '../../../components/ui/Modal';
import { tripService } from '../../../services/tripService';
import { inventoryService } from '../../../services/inventoryService';
import { mealService } from '../../../services/mealService';

const DataTab = ({ tripData }) => {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExportInventory = async () => {
    setExporting(true);
    try {
      const { data: items } = await inventoryService?.getInventoryItems(tripData?.id);
      
      if (items && items?.length > 0) {
        const csvContent = [
          ['Item Name', 'Category', 'Quantity', 'Unit', 'Storage Location'],
          ...items?.map(item => [
            item?.itemName,
            item?.category,
            item?.quantity,
            item?.unit,
            item?.storageLocation || 'N/A'
          ])
        ]?.map(row => row?.join(','))?.join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL?.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${tripData?.tripName}-inventory.csv`;
        a?.click();
        window.URL?.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
    setExporting(false);
  };

  const handleExportMealPlan = async () => {
    setExporting(true);
    try {
      const { data: meals } = await mealService?.getMealsByTrip(tripData?.id);
      
      if (meals && meals?.length > 0) {
        const csvContent = [
          ['Day', 'Meal Type', 'Meal Name', 'Ingredients'],
          ...meals?.map(meal => [
            meal?.dayNumber,
            meal?.mealType,
            meal?.mealName,
            meal?.ingredients || 'N/A'
          ])
        ]?.map(row => row?.join(','))?.join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL?.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${tripData?.tripName}-meal-plan.csv`;
        a?.click();
        window.URL?.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
    setExporting(false);
  };

  const handleDeleteTrip = async () => {
    setDeleting(true);
    const { error } = await tripService?.deleteTrip(tripData?.id);
    
    if (!error) {
      navigate('/trip-setup');
    } else {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          Trip Data Management
        </h2>
      </div>

      <div className="space-y-4">
        <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-background)', borderColor: 'var(--color-border)' }}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                Export Inventory
              </h3>
              <p className="text-xs text-muted-foreground">
                Download your current inventory as a CSV file for backup or external use
              </p>
            </div>
            <Button
              onClick={handleExportInventory}
              variant="outline"
              size="sm"
              disabled={exporting}
            >
              <Icon name="Download" size={16} />
              <span className="ml-2">{exporting ? 'Exporting...' : 'Export'}</span>
            </Button>
          </div>
        </div>

        <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-background)', borderColor: 'var(--color-border)' }}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                Export Meal Plan
              </h3>
              <p className="text-xs text-muted-foreground">
                Download your complete meal plan as a CSV file
              </p>
            </div>
            <Button
              onClick={handleExportMealPlan}
              variant="outline"
              size="sm"
              disabled={exporting}
            >
              <Icon name="Download" size={16} />
              <span className="ml-2">{exporting ? 'Exporting...' : 'Export'}</span>
            </Button>
          </div>
        </div>

        <div className="p-4 rounded-lg border border-red-500/20" style={{ backgroundColor: 'var(--color-background)' }}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-sm font-medium mb-1 text-red-500">
                Delete Trip
              </h3>
              <p className="text-xs text-muted-foreground">
                Permanently delete this trip and all associated data. This action cannot be undone.
              </p>
            </div>
            <Button
              onClick={() => setShowDeleteModal(true)}
              variant="outline"
              size="sm"
              className="border-red-500/20 text-red-500 hover:bg-red-500/10"
            >
              <Icon name="Trash2" size={16} />
              <span className="ml-2">Delete</span>
            </Button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Trip"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete <strong>{tripData?.tripName}</strong>? This will permanently remove:
          </p>
          <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
            <li>All trip configuration and settings</li>
            <li>Complete inventory data</li>
            <li>All meal plans and selections</li>
            <li>Shopping lists and logs</li>
          </ul>
          <p className="text-sm font-medium text-red-500">
            This action cannot be undone.
          </p>
          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleDeleteTrip}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600"
            >
              {deleting ? 'Deleting...' : 'Delete Trip'}
            </Button>
            <Button
              onClick={() => setShowDeleteModal(false)}
              variant="outline"
              disabled={deleting}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DataTab;